import React from 'react';
import { cn } from '../../../utils/cn';

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Height of the skeleton */
  height?: string;
  /** Width of the skeleton */
  width?: string;
  /** Number of lines for text skeleton */
  lines?: number;
  /** Whether to show a circular skeleton */
  circle?: boolean;
}

export const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(
  ({ className, height = '1rem', width = '100%', lines = 1, circle = false, ...props }, ref) => {
    if (lines > 1) {
      return (
        <div className={cn('space-y-2', className)} ref={ref} {...props}>
          {Array.from({ length: lines }).map((_, index) => (
            <div
              key={index}
              className="animate-pulse bg-neutral-200 dark:bg-neutral-700 rounded"
              style={{
                height: '1rem',
                width: index === lines - 1 ? '75%' : '100%',
              }}
            />
          ))}
        </div>
      );
    }

    return (
      <div
        className={cn(
          'animate-pulse bg-neutral-200 dark:bg-neutral-700',
          circle ? 'rounded-full' : 'rounded',
          className
        )}
        style={{ height, width }}
        ref={ref}
        {...props}
      />
    );
  }
);

Skeleton.displayName = 'Skeleton';

export const SkeletonText: React.FC<{ lines?: number; className?: string }> = ({ 
  lines = 3, 
  className 
}) => (
  <Skeleton lines={lines} className={className} />
);

export const SkeletonCard: React.FC<{ className?: string }> = ({ className }) => (
  <div className={cn('p-6 space-y-4', className)}>
    <Skeleton height="200px" className="rounded-lg" />
    <div className="space-y-2">
      <Skeleton height="1.5rem" width="60%" />
      <Skeleton height="1rem" width="40%" />
    </div>
    <Skeleton lines={3} />
    <div className="flex gap-2">
      <Skeleton height="2rem" width="80px" className="rounded-full" />
      <Skeleton height="2rem" width="100px" className="rounded-full" />
    </div>
  </div>
);